// src/pages/CartListPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, Alert, IconButton, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCartStore } from '../../store/cartStore';
import { useUserStore } from '../../store/userStore';
import { formatCurrency } from '../../utils/currency';

function formatDate(dateStr?: string | null) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  }).format(d);
}
const statusToColor = (s?: string | null) =>
  s === 'placed' ? 'success' : s === 'cancelled' ? 'default' : 'warning';

export default function CartListPage() {
  const navigate = useNavigate();
  const { history, historyLoading, historyError, loadHistory } = useCartStore();
  const user = useUserStore(s => s.user);
  const isVendor = useMemo(() => (user?.role ?? '').toUpperCase().includes('VENDOR'), [user?.role]);

  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    await loadHistory();
    setRefreshing(false);
  };

  useEffect(() => { void refresh(); /* mount */ }, []);

  const empty = !historyLoading && (history?.length ?? 0) === 0;

  const handleRowNav = (cartId: number, status?: string | null) => {
    const s = (status ?? '').toLowerCase();
    if (s === 'placed' || s === 'cancelled') navigate(`/cart-detail/${cartId}`);
    else navigate(`/cart/${cartId}`);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Carts</Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={refresh} disabled={historyLoading || refreshing}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}

      {empty ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>No carts found</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isVendor ? 'Add parts to create a cart.' : 'There are no carts to display.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>Continue browsing</Button>
        </Paper>
      ) : (
        <Paper sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 120 }}>Cart #</TableCell>
                {!isVendor && <TableCell sx={{ width: 120 }}>Vendor ID</TableCell>}
                <TableCell sx={{ width: 220 }}>Created on</TableCell>
                <TableCell sx={{ width: 140 }}>Status</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Total Qty</TableCell>
                <TableCell align="right" sx={{ width: 160 }}>Total Amount</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((h) => {
                const s = (h.status ?? '').toLowerCase();
                const isReadonly = s === 'placed' || s === 'cancelled';
                return (
                  <TableRow
                    key={h.cartId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowNav(h.cartId, h.status)}
                  >
                    <TableCell>#{h.cartId}</TableCell>
                    {!isVendor && <TableCell>{h.vendorId ?? '-'}</TableCell>}
                    <TableCell>{formatDate(h.createdOn)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={(h.status ?? '').toUpperCase() || 'OPEN'}
                        color={statusToColor(h.status) as any}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{h.totalQuantity ?? 0}</TableCell>
                    <TableCell align="right">{formatCurrency(h.totalAmount)}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowNav(h.cartId, h.status);
                        }}
                      >
                        {isReadonly ? 'Details' : 'Open'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
